// src/components/frontpage/DirectoryNav.jsx
// 8-category directory navigation for the Locality sidebar.
// Compact vertical list: ~28px row height, 13px text, icon + label + count badge.
// Click a category to open a 240px right-aligned viewport panel listing contacts.
// Click a contact to open a detail pop-out with full fields + tel/mailto/external links.

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';
import { tokens } from '../../styles/designTokens';
import PresencePanel from '../presence/PresencePanel';
import { listingToPresence } from '../presence/presenceSource';
import { SectionTitle, DirectoryRow, AccordionBranch, EntryBullet, DIR_FONT, openEntryStyle, OpenEntryFrame, AreaLabel } from './DirectorySection';
import { compareByDistance } from '../../data/directoryService';
import { useExpandInPlace } from '../../hooks/useExpandInPlace';
import { formatWebsiteDisplay } from '../../utils/formatText';
import { CONTACT_GLYPH } from '../../utils/contactFieldIcons';

// Known categories — exact names and order per build brief.
// Any listing whose category doesn't match gets bucketed into 'other'.
const DIRECTORY_CATEGORIES = [
  { id: 'health', icon: '\u{1F3E5}' },
  { id: 'home', icon: '\u{1F3E0}' },
  { id: 'official', icon: '\u{1F3DB}\uFE0F' },
  { id: 'learning', icon: '\u{1F4DA}' },
  { id: 'food_shopping', icon: '\u{1F6D2}' },
  { id: 'activities', icon: '\u26BD' },
  { id: 'transport', icon: '\u{1F68C}' },
  { id: 'eating_drinking', icon: '\u{1F37D}\uFE0F' }
];

const KNOWN_CATEGORY_IDS = new Set(DIRECTORY_CATEGORIES.map(c => c.id));

// Shared heading style (section title + viewport panel header)
const HEADING_STYLE = {
  fontFamily: 'OpenSauceSans, sans-serif',
  fontWeight: '700',
  fontSize: '14px',
  color: tokens.colors.contextLocality,
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const DirectoryNav = ({ compact = false, listings = [], onSuggestCorrection, drillStyle = 'popout', openListingId, onToggleListing, openCategoryKey, onToggleCategory, isOpen = true, onToggleSection = null }) => {
  const { t } = useTranslation('frontpage');
  const theme = useTheme();
  // Drill style selects how a category opens: 'accordion' = inline expansion (mobile, and the
  // desktop Directory column); 'popout' = 240px fixed panel + DetailPopout (desktop sidebar).
  const useAccordion = drillStyle === 'accordion';
  // Single source for row rhythm — the top-level category rows are the density benchmark;
  // the expanded sub-listing rows reuse the exact same minimum so both stack on one cadence.
  // minHeight (not height) so a long name can wrap to a second line instead of clipping.
  const rowHeight = compact ? '22px' : '26px';
  const [panelPos, setPanelPos] = useState(null);
  const navRef = useRef(null);
  const panelRef = useRef(null);
  const detailRef = useRef(null);

  // Open-entry card: controlled by the parent (DirectoryColumn shares one id across all sections)
  // or, when those props are absent (SidebarLocality popout), fall back to local state so the
  // sidebar keeps its own independent open card. See src/hooks/useExpandInPlace.js.
  const listingGroup = useExpandInPlace({ openId: openListingId, onToggle: onToggleListing });
  const openEntryId = listingGroup.openId;
  const toggleListing = listingGroup.toggle;

  // Open category: shared across all sections when controlled (DirectoryColumn owns one namespaced
  // open-category key), else local (SidebarLocality popout keeps its own). The shared key is
  // namespaced 'directory:<id>' so it can never collide with the utility section's keys; internally
  // we work in the raw category id. Toggling a category clears the open card (rule 2) — in
  // controlled mode the parent does it (DirectoryColumn's handleToggleCategory); in local mode we
  // clear the listing group here.
  const CAT_NS = 'directory:';
  const categoryGroup = useExpandInPlace({ openId: openCategoryKey, onToggle: onToggleCategory });
  const openCategoryId = typeof categoryGroup.openId === 'string' && categoryGroup.openId.startsWith(CAT_NS)
    ? categoryGroup.openId.slice(CAT_NS.length)
    : null;
  const toggleCategory = (catId) => {
    categoryGroup.toggle(CAT_NS + catId);
    if (!categoryGroup.controlled) toggleListing(null);
  };
  const closeCategory = () => {
    categoryGroup.toggle(null);
    if (!categoryGroup.controlled) toggleListing(null);
  };

  // Bold-only-when-linked: a name renders bold only when the listing links to a verified group
  // front page — the same predicate that drives the "View group" affordance.
  const isLinked = (l) => l && l.claim_status === 'verified' && !!l.claimed_by_group_id;

  // Group listings by category, with an 'other' bucket for unmatched
  const grouped = useMemo(() => {
    const map = {};
    (listings || []).forEach(listing => {
      const cat = KNOWN_CATEGORY_IDS.has(listing.category) ? listing.category : 'other';
      if (!map[cat]) map[cat] = [];
      map[cat].push(listing);
    });
    // Local first (Roadmap #87, R2): own listings, then wider ones nearest first, then national;
    // the name decides within each. A plain A–Z sort here would undo the read's order.
    Object.values(map).forEach(arr => arr.sort((a, b) => compareByDistance(a, b) || (a.name || '').localeCompare(b.name || '')));
    return map;
  }, [listings]);

  // Build display list: known categories + 'other' if needed
  const displayCategories = useMemo(() => {
    const cats = DIRECTORY_CATEGORIES.map(cat => ({
      ...cat,
      count: (grouped[cat.id] || []).length
    }));
    if (grouped.other && grouped.other.length > 0) {
      cats.push({ id: 'other', icon: '\u{1F4C2}', count: grouped.other.length });
    }
    return cats;
  }, [grouped]);

  // The currently-open listing object (for the pop-out detail render), resolved from the shared
  // open-entry id within the open category.
  const detailListing = openEntryId
    ? (grouped[openCategoryId] || []).find(l => l.id === openEntryId) || null
    : null;

  // Close detail/category on outside click (pop-out affordance) or Escape.
  useEffect(() => {
    if (!openCategoryId) return;

    // Outside-click dismiss is a POP-OUT affordance only. In accordion mode panelRef/detailRef
    // are null, so an outside mousedown — including a click on another category header — would
    // read as "outside" and collapse the open category, eating the switch. Register it for the
    // pop-out drill style only; the inline accordion toggles purely on header click.
    let handleMouseDown;
    if (!useAccordion) {
      handleMouseDown = (ev) => {
        if (detailRef.current && detailRef.current.contains(ev.target)) return;
        if (panelRef.current && panelRef.current.contains(ev.target)) {
          if (openEntryId) toggleListing(null);
          return;
        }
        closeCategory();
      };
      document.addEventListener('mousedown', handleMouseDown);
    }

    // Escape-to-close applies in both drill styles.
    const handleEsc = (ev) => {
      if (ev.code === 'Escape') {
        if (openEntryId) {
          toggleListing(null);
        } else {
          closeCategory();
        }
      }
    };
    document.addEventListener('keydown', handleEsc);

    return () => {
      if (handleMouseDown) document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [openCategoryId, openEntryId, useAccordion]);

  const handleToggle = (catId) => {
    const willClose = openCategoryId === catId;
    // Pop-out positioning is a pop-out-drill concern only; accordion uses inline expansion.
    if (!useAccordion) {
      if (willClose) {
        setPanelPos(null);
      } else if (navRef.current) {
        const rect = navRef.current.getBoundingClientRect();
        setPanelPos({
          top: rect.top,
          right: window.innerWidth - rect.right,
          height: Math.max(200, window.innerHeight - rect.top - 40)
        });
      }
    }
    // Toggle the (shared) category; this clears the open card — the card lived in the category
    // being left (controlled: parent clears it; local: toggleCategory clears localOpenId).
    toggleCategory(catId);
  };

  const openListings = openCategoryId ? (grouped[openCategoryId] || []) : [];

  return (
    <div ref={navRef} style={{ position: 'relative' }}>
      {/* Section title — always visible now (16 Aug fix): this used to be suppressed via a
          hideHeading prop on the assumption that ModeColumn's own column header already labelled
          the column "Directory" — true only when that header isn't ALSO hidden, which it always
          is on both live callers (LocalityFrontPage.jsx, MobileFrontPage.jsx both pass
          hideColumnHeader={true}), so the category list rendered with no heading at all. Now the
          section's own toggle control, matching ContactList/UtilityCategoryList exactly. */}
      <SectionTitle title={t('directory')} onClick={onToggleSection} />

      {/* Body — closed sections render only their header above. isOpen/onToggleSection are the
          Directory column shell brief's Scope 3 concertina; content-level state (category/listing)
          below is unchanged from before that scope. */}
      {isOpen && (
      <>
      {/* Category rows with count badges */}
      {displayCategories.map(cat => (
        <React.Fragment key={cat.id}>
          <DirectoryRow
            icon={cat.icon}
            label={t(`dir_${cat.id}`)}
            count={cat.count}
            isOpen={openCategoryId === cat.id}
            onClick={() => handleToggle(cat.id)}
            compact={compact}
          />

          {/* INLINE ACCORDION — expanded listings below the category row (mobile + desktop Directory column) */}
          {useAccordion && openCategoryId === cat.id && (
            <AccordionBranch>
              {(grouped[cat.id] || []).length === 0 ? (
                <div style={{ fontSize: '12px', color: '#9ca3af', padding: '8px 0' }}>
                  {t('dir_empty')}
                </div>
              ) : (
                (grouped[cat.id] || []).map(listing => (
                  <div
                    key={listing.id}
                    onClick={() => toggleListing(listing.id)}
                    style={{
                      padding: '0 4px',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      fontFamily: DIR_FONT
                    }}
                  >
                    {/* Border-encloses-name correction, 16 Aug: OpenEntryFrame wraps the row AND
                        its detail together, so the open-entry border frames the name, not just
                        the detail beneath it. */}
                    <OpenEntryFrame isOpen={openEntryId === listing.id}>
                      {/* Collapsed summary \u2014 leading context dot + name, on the same row cadence as the
                          category rows. minHeight + flex-start so a long name wraps and line 2 hangs under
                          line 1. Bold only when the listing links to a verified group front page. */}
                      <div style={{
                        minHeight: rowHeight, display: 'flex', alignItems: 'flex-start', gap: '8px',
                        padding: '2px 0'
                      }}>
                        <EntryBullet />
                        <span style={{
                          fontWeight: isLinked(listing) ? '700' : '400', fontSize: '13px',
                          lineHeight: '1.35', color: '#1f2937'
                        }}>
                          {listing.name}
                          <AreaLabel row={listing} />
                        </span>
                      </div>
                      {/* Inline detail \u2014 the thin, listing-backed Presence render. */}
                      {openEntryId === listing.id && (
                        <div
                          onClick={(ev) => ev.stopPropagation()}
                          style={{ marginTop: '4px' }}
                        >
                          <PresencePanel
                            source={listingToPresence(listing)}
                            density="compact"
                            onSuggestCorrection={onSuggestCorrection ? () => onSuggestCorrection(listing) : undefined}
                            onClose={() => toggleListing(null)}
                          />
                        </div>
                      )}
                    </OpenEntryFrame>
                  </div>
                ))
              )}
            </AccordionBranch>
          )}
        </React.Fragment>
      ))}

      {/* VIEWPORT PANEL — 240px, fixed position anchored to nav container (popout drill style) */}
      {!useAccordion && openCategoryId && panelPos && (
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: `${panelPos.top}px`,
            right: `${panelPos.right}px`,
            width: '240px',
            height: `${panelPos.height}px`,
            background: 'white',
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            zIndex: 200,
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'OpenSauceSans, sans-serif',
            overflow: 'hidden'
          }}
        >
          {/* Panel header */}
          <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
            <div style={{ ...HEADING_STYLE, fontSize: '13px' }}>
              {t(`dir_${openCategoryId}`)}
            </div>
          </div>

          {/* Panel body — scrollable */}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 14px 14px' }}>
            {openListings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px' }}>
                  {t('dir_empty')}
                </div>
                {onSuggestCorrection && (
                  <button
                    onClick={() => { closeCategory(); onSuggestCorrection(null); }}
                    style={{
                      background: 'none', border: '1px dashed #a0c4e8', borderRadius: '6px',
                      padding: '6px 12px', fontFamily: 'OpenSauceSans, sans-serif',
                      fontSize: '11px', fontWeight: '600', color: tokens.colors.contextLocality, cursor: 'pointer'
                    }}
                  >
                    + {t('suggest_listing')}
                  </button>
                )}
              </div>
            ) : (
              openListings.map((listing, idx) => (
                <button
                  key={listing.id}
                  onClick={() => toggleListing(listing.id)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', padding: '6px 4px',
                    background: openEntryId === listing.id ? '#f0f7ff' : 'none',
                    border: 'none',
                    borderBottom: idx < openListings.length - 1 ? '1px solid #f0f0f0' : 'none',
                    cursor: 'pointer', fontFamily: 'OpenSauceSans, sans-serif',
                    borderRadius: '4px', transition: 'background 0.15s'
                  }}
                  onMouseEnter={(ev) => {
                    if (openEntryId !== listing.id) ev.currentTarget.style.background = '#f9fafb';
                  }}
                  onMouseLeave={(ev) => {
                    if (openEntryId !== listing.id) ev.currentTarget.style.background = 'none';
                  }}
                >
                  <div style={{ fontWeight: isLinked(listing) ? '700' : '400', fontSize: '13px', color: '#1f2937' }}>
                    {listing.name}
                    <AreaLabel row={listing} />
                  </div>
                  {listing.tags && listing.tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '3px' }}>
                      {listing.tags.map(tag => (
                        <span key={tag} style={{
                          display: 'inline-block', padding: '1px 6px', background: '#e8edf3',
                          borderRadius: '3px', fontSize: '10px', color: '#6b7280', fontWeight: '500'
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* DETAIL POP-OUT — contact details for a single listing (popout drill style) */}
      {!useAccordion && detailListing && panelPos && (
        <DetailPopout
          listing={detailListing}
          onClose={() => toggleListing(null)}
          onSuggestCorrection={onSuggestCorrection}
          onCloseAll={() => { toggleListing(null); closeCategory(); setPanelPos(null); }}
          detailRef={detailRef}
          panelPos={panelPos}
          t={t}
          theme={theme}
        />
      )}
      </>
      )}
    </div>
  );
};

// ============================================================================
// DETAIL POP-OUT — full listing fields with tel/mailto/external links
// ============================================================================

const DetailPopout = ({ listing, onCloseAll, onSuggestCorrection, detailRef, panelPos, t, theme }) => {
  // Panel right edge in viewport coords = window.innerWidth - panelPos.right
  // Place detail 8px to the right of the panel, clamped so it doesn't overflow
  const detailLeft = Math.min(
    window.innerWidth - panelPos.right + 8,
    window.innerWidth - 248
  );

  return (
  <div
    ref={detailRef}
    style={{
      position: 'fixed',
      top: `${panelPos.top + 20}px`,
      left: `${detailLeft}px`,
      width: '240px',
      background: 'white', borderRadius: '10px', padding: '14px 16px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)', zIndex: 210,
      fontFamily: 'OpenSauceSans, sans-serif',
      // Border marks this entry's detail panel only (border discipline correction, 16 Aug).
      ...openEntryStyle(theme)
    }}
  >
    <div style={{ fontWeight: '700', fontSize: '14px', color: '#1f2937', marginBottom: '8px' }}>
      {listing.name}
    </div>

    {listing.tags && listing.tags.length > 0 && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '8px' }}>
        {listing.tags.map(tag => (
          <span key={tag} style={{
            display: 'inline-block', padding: '2px 8px', background: '#e8edf3',
            borderRadius: '4px', fontSize: '11px', color: '#6b7280', fontWeight: '500'
          }}>
            {tag}
          </span>
        ))}
      </div>
    )}

    {listing.phone && (
      <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>
        {CONTACT_GLYPH.phone}{' '}
        <a href={`tel:${listing.phone}`} style={{ color: tokens.colors.contextLocality, textDecoration: 'none' }}>
          {listing.phone}
        </a>
      </div>
    )}

    {listing.email && (
      <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>
        {CONTACT_GLYPH.email}{' '}
        <a href={`mailto:${listing.email}`} style={{ color: tokens.colors.contextLocality, textDecoration: 'none' }}>
          {listing.email}
        </a>
      </div>
    )}

    {listing.website && (
      <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>
        {CONTACT_GLYPH.website}{' '}
        <a
          href={listing.website.startsWith('http') ? listing.website : `https://${listing.website}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: tokens.colors.contextLocality, textDecoration: 'none' }}
        >
          {formatWebsiteDisplay(listing.website)}
        </a>
      </div>
    )}

    {listing.address && (
      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
        {CONTACT_GLYPH.address} {listing.address}
      </div>
    )}

    {listing.description && (
      <div style={{
        fontSize: '12px', color: '#6b7280', marginTop: '6px', lineHeight: '1.4',
        borderTop: '1px solid #f0f0f0', paddingTop: '6px'
      }}>
        {listing.description}
      </div>
    )}

    {onSuggestCorrection && (
      <button
        onClick={(ev) => {
          ev.stopPropagation();
          onCloseAll();
          onSuggestCorrection(listing);
        }}
        style={{
          display: 'block', marginTop: '10px', background: 'none', border: 'none',
          padding: 0, cursor: 'pointer', fontFamily: 'OpenSauceSans, sans-serif',
          fontSize: '11px', fontWeight: '600', color: tokens.colors.contextLocality
        }}
      >
        {t('suggest_correction')} {'\u2192'}
      </button>
    )}
  </div>
  );
};

export default DirectoryNav;
